/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { styled, type Style } from 'inlines'

export const APPEAR_ANIMATION = 'story-card-appear 500ms ease-out'
export const APPEAR_STAGGER_MS = 225

type AppearMode = 'hidden' | 'animated' | 'static'

const AppearSectionContext = createContext<AppearMode | null>(null)

export type AppearSectionProps = {
  children: ReactNode
  style?: Style
  disableAnimations?: boolean
}

export const AppearSection = ({
  children,
  style,
  disableAnimations,
}: AppearSectionProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(disableAnimations === true)

  useEffect(() => {
    if (disableAnimations) {
      return
    }

    const element = ref.current
    if (!element) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '0px 0px -5% 0px', threshold: 0.05 },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [disableAnimations])

  const mode: AppearMode = disableAnimations
    ? 'static'
    : isVisible
      ? 'animated'
      : 'hidden'

  return (
    <AppearSectionContext.Provider value={mode}>
      <styled.div ref={ref} style={style}>
        {children}
      </styled.div>
    </AppearSectionContext.Provider>
  )
}

export const useAppearStyle = (staggerIndex = 0): Style | undefined => {
  const mode = useContext(AppearSectionContext)
  if (mode === null) {
    return undefined
  }
  if (mode === 'hidden') {
    return { opacity: 0, visibility: 'hidden', pointerEvents: 'none' }
  }
  if (mode === 'static') {
    return undefined
  }
  return {
    visibility: 'visible',
    animation: `${APPEAR_ANIMATION} ${staggerIndex * APPEAR_STAGGER_MS}ms both`,
  }
}

export type AppearItemProps = {
  children: ReactNode
  staggerIndex?: number
  style?: Style
}

export const AppearItem = ({
  children,
  staggerIndex = 0,
  style,
}: AppearItemProps) => {
  const appearStyle = useAppearStyle(staggerIndex)
  return (
    <styled.div style={{ ...appearStyle, ...style }}>{children}</styled.div>
  )
}
